import React from 'react';
import { ArrowRightIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { SubscriptionPlan } from '../../types/subscription';

interface PricingCardProps {
  plan: SubscriptionPlan;
  isCurrentPlan: boolean;
  onSelect: () => void;
  loading: boolean;
  billingCycle: 'monthly' | 'annual';
}

const planThemes: Record<string, {
  glow: string;
  chip: string;
  surface: string;
}> = {
  FREE: {
    glow: 'from-slate-200 via-slate-100 to-white',
    chip: 'bg-slate-100 text-slate-700 border-slate-200',
    surface: 'border-slate-200',
  },
  BASIC: {
    glow: 'from-sky-200 via-cyan-100 to-white',
    chip: 'bg-sky-100 text-sky-700 border-sky-200',
    surface: 'border-sky-200',
  },
  PREMIUM: {
    glow: 'from-amber-200 via-orange-100 to-white',
    chip: 'bg-amber-100 text-amber-800 border-amber-200',
    surface: 'border-amber-200',
  },
  ENTERPRISE: {
    glow: 'from-emerald-200 via-teal-100 to-white',
    chip: 'bg-emerald-100 text-emerald-800 border-emerald-200',
    surface: 'border-emerald-200',
  },
};

const currencySymbols: Record<string, string> = {
  INR: 'Rs.',
  USD: '$',
};

export const PricingCard: React.FC<PricingCardProps> = ({
  plan,
  isCurrentPlan,
  onSelect,
  loading,
  billingCycle,
}) => {
  const theme = planThemes[plan.planType] || planThemes.FREE;

  const getPrice = () => {
    const basePrice = Number(plan.price || 0);
    if (billingCycle === 'annual') {
      return Math.floor(basePrice * 12 * 0.8);
    }
    return basePrice;
  };

  const currencySymbol = currencySymbols[plan.currency || 'INR'] || (plan.currency || 'INR');
  const featureList = [
    plan.features.maxExamAttempts === -1
      ? 'Unlimited practice attempts'
      : `Up to ${plan.features.maxExamAttempts ?? plan.features.maxAssignments ?? 0} practice attempts`,
    plan.features.examResultsAccess ? 'Detailed exam results' : 'Basic exam results',
    plan.features.hasAnalytics ? 'Learning analytics' : 'No analytics dashboard',
    plan.features.certificateGeneration ? 'Certificate generation' : 'Certificates not included',
    `Support: ${String(plan.features.supportLevel || 'email').toUpperCase()}`,
  ];

  if (plan.features.hasAdvancedReporting) featureList.push('Advanced reporting');
  if (plan.features.canCreateQuizzes) featureList.push('Practice quiz access');
  if (plan.features.storageLimit) featureList.push(`${plan.features.storageLimit} GB notes storage`);

  return (
    <article
      className={`relative overflow-hidden rounded-[2rem] border bg-white p-6 shadow-[0_24px_80px_-48px_rgba(15,23,42,0.32)] transition hover:-translate-y-1 hover:shadow-[0_30px_90px_-46px_rgba(15,23,42,0.4)] ${
        isCurrentPlan ? 'border-emerald-300' : theme.surface
      } ${plan.planType === 'PREMIUM' ? 'lg:-mt-3' : ''}`}
    >
      <div className={`absolute inset-x-0 top-0 h-28 bg-gradient-to-br ${theme.glow}`} />
      <div className="relative">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${theme.chip}`}>
                {plan.planType}
              </span>
              {plan.planType === 'PREMIUM' && !isCurrentPlan ? (
                <span className="rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-700">
                  Popular
                </span>
              ) : null}
            </div>
            <h3 className="mt-4 text-2xl font-semibold tracking-tight text-slate-950">{plan.name}</h3>
          </div>

          {isCurrentPlan ? (
            <span className="rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">
              Current
            </span>
          ) : null}
        </div>

        <p className="mt-4 min-h-[72px] text-sm leading-6 text-slate-600">
          {plan.description || 'Plan details become available as soon as you select this tier.'}
        </p>

        <div className="mt-6 rounded-[1.5rem] border border-slate-200 bg-slate-50 px-5 py-4">
          <div className="flex items-end gap-2">
            <span className="text-4xl font-semibold tracking-tight text-slate-950">
              {currencySymbol}
              {getPrice().toFixed(0)}
            </span>
            <span className="pb-1 text-sm font-medium text-slate-500">
              / {billingCycle === 'monthly' ? 'month' : 'year'}
            </span>
          </div>
          <p className="mt-2 text-xs uppercase tracking-[0.18em] text-slate-400">
            billed in {plan.currency || 'INR'}
          </p>
        </div>

        <div className="mt-5 space-y-3">
          {featureList.slice(0, 6).map((feature) => (
            <div key={feature} className="flex items-start gap-3 rounded-[1.25rem] border border-slate-200 bg-white px-4 py-3">
              <CheckCircleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-emerald-600" />
              <span className="text-sm text-slate-700">{feature}</span>
            </div>
          ))}
        </div>

        <button
          onClick={onSelect}
          disabled={loading || isCurrentPlan}
          className={`mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 ${
            isCurrentPlan
              ? 'border border-slate-200 bg-slate-100 text-slate-400'
              : 'bg-slate-950 text-white hover:bg-slate-800'
          }`}
        >
          {isCurrentPlan ? 'Selected plan' : loading ? 'Loading...' : 'Select plan'}
          {!isCurrentPlan && !loading ? <ArrowRightIcon className="h-4 w-4" /> : null}
        </button>
      </div>
    </article>
  );
};

export default PricingCard;
